// Home.js
import React, { useMemo, useState, useEffect } from "react";
import { getAuth } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import "./Home.css";
import ArchetypeSimulator from "./ArchetypeSimulator";

// Keep your comparison table and pricing
import FluencyComparisonGrid from "./FluencyComparisonGrid";
import PricingCards from "./PricingCards";

/**
 * Image helper (CRA/Webpack)
 * Dynamically load images that live in the same folder as this component.
 * Any missing filename is safely ignored (returns null).
 */
const ctx = require.context("./", false, /\.(png|jpe?g|gif|svg)$/);
const img = (name) => {
  if (!name) return null;
  try {
    return ctx(`./${name}`);
  } catch {
    return null;
  }
};

const getPilotPhase = (pilot) => {
  if (!pilot) return "none";
  const now = new Date();
  const start = new Date(pilot.startAt._seconds * 1000);
  const end = new Date(pilot.endAt._seconds * 1000);
  const earlyWindow = new Date(start);
  earlyWindow.setDate(earlyWindow.getDate() - 30);

  if (now < earlyWindow) return "tooEarly";
  if (now < start) return "prelaunch";
  if (now <= end) return "active";
  return "finished";
};

/**
 * Category inventories (derived from your Files-by-Category lists).
 * Feel free to tweak routes or add/remove filenames—missing files are skipped automatically.
 */
const INVENTORY = {
  fluency: {
    label: "Fluency Tools & Practice",
    route: "/fluency", // update to your actual route if different
    items: [
      "Fluency Arena.png",
      "Fluency Engine.png",
      "Fluency Expanders.png",
      "Fluency Practice.png",
      "Fluency Speech Plans.png",
      "Impulse Meter.png",
      "Pronounce.png",
      "Speech Grid.png",
      "Speech Performance Planner.png",
      "Talk with AI.png",
      "Talk Topics.png",
    ],
  },
  expanders: {
    label: "Language Expanders",
    route: "/resources", // update as needed
    items: [
      "Conversation Enders.png",
      "Culture Expanders.png",
      "Grammar Expanders.png",
      "Idiom Expanders.png",
      "Logic Expanders.png",
      "Persuasion Expanders.png",
      "Vocabulary List.png",
      "Seen Vocabulary.png",
    ],
  },
  assess: {
    label: "Assessments & Testing",
    route: "/assessments",
    items: [
      "Admin Assessments.png",
      "Self Assessments.png",
      "IELTS Tester.png",
      "IELTS Test Results.png",
    ],
  },
  collab: {
    label: "Projects, Tasks & Collaboration",
    route: "/train", // Train.js / project tools
    items: [
      "Project Delivery Grid.png",
      "Task Card Grid.png",
      "Task Creator.png",
      "Miro Whiteboard.png",
      "Function Selector.png",
      "Overlay.png",
      "ShareWall.png",
      "Chatroom.png",
    ],
  },
  media: {
    label: "Media, Transcripts & Reference",
    route: "/studio", // podcast/media hub
    items: [
      "Video Transcript.png",
      "Sir Ian McKellen Rime of the Ancient Mariner 1raSUYAr0s0 (65).png",
      "Phonemiogram.png",
      "harkness.jpg",
    ],
  },
  voicebots: {
    label: "VoiceBots & Characters",
    route: "/voicebot",
    items: [
      "Cooper.png",
      "Cooper VoiceBot.png",
      "Malika.png",
      "Malika VoiceBot.png",
      "Stephane.png",
      "Stephane VoiceBot.png",
      "Vivian.png",
      "Vivian VoiceBot.png",
      "iSpeakWell Studio Cooper.png",
      "iSpeakWell Studio Stephane.png",
      "iSpeakWell VoiceBot.png",
    ],
  },
};

/** Pick a hero image from candidates that actually exist */
const heroCandidates = [
  "iSpeakWell VoiceBot.png",
  "iSpeakWell Studio Cooper.png",
  "Cooper.png",
  "Cooper VoiceBot.png",
  "Malika.png",
  "Malika VoiceBot.png",
  "Stephane.png",
  "Stephane VoiceBot.png",
  "Vivian.png",
  "Vivian VoiceBot.png",
  "iSpeakWell Studio Cooper.png",
  "iSpeakWell VoiceBot.png",
  "iSW Banner 1.png",
  "iSW Banner 2.png",
  "iSW Banner 3.png",
  "iSW Banner 4.png",
];
const heroImages = heroCandidates.map(img).filter(Boolean);

/** Pick a notebook/studio-style image (fallback to any available) */
const notebookCandidates = [
  "UnsplashImage.jpg",
  "iSpeakWell Studio Cooper.png",
  "iSpeakWell Studio Stephane.png",
  "Video Transcript.png",
];
const notebookImages = notebookCandidates.map(img).filter(Boolean);

/** Small card for each category tile */
function CategoryCard({ title, items, onClick }) {
  const thumbs = useMemo(() => items.map(img).filter(Boolean).slice(0, 5), [items]);
  return (
    <button className="hp-cat-card" onClick={onClick} aria-label={`Open ${title} showcase`}>
      <div className="hp-cat-glow" />
      <div className="hp-showcase-head">{title}</div>
      <div className="hp-polaroids">
        {thumbs.map((src, i) => (
          <div key={i} className={`hp-polaroid tilt-${(i % 5) + 1}`}>
            <img src={src} alt={`${title} ${i + 1}`} loading="lazy" />
          </div>
        ))}
      </div>
      <div className="hp-cat-footer">Open Showcase</div>
    </button>
  );
}

function ShowcaseOverlay({ bucket, onClose }) {
  const [activeIndex, setActiveIndex] = useState(null);

  // Call hooks unconditionally
  const imgs = useMemo(() => {
    return bucket ? bucket.items.map(img).filter(Boolean) : [];
  }, [bucket]);

  useEffect(() => {
    if (activeIndex === null || imgs.length === 0) return;

    const handleKey = (e) => {
      if (e.key === "Escape") setActiveIndex(null);
      if (e.key === "ArrowRight") {
        setActiveIndex((i) => (i + 1) % imgs.length);
      }
      if (e.key === "ArrowLeft") {
        setActiveIndex((i) => (i - 1 + imgs.length) % imgs.length);
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [activeIndex, imgs]);

  if (!bucket) return null; // ✅ safe now, hooks already ran

  return (
    <div className="hp-showcase" role="dialog" aria-modal="true">
      <div className="hp-showcase-backdrop" onClick={onClose} />
      <div className="hp-showcase-card">
        <div className="hp-showcase-head">
          <h4>{bucket.label}</h4>
          <div className="hp-showcase-actions">
            <button className="hp-btn hp-btn-primary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
        <div className="hp-gallery">
          {imgs.map((src, i) => (
            <img
              key={i}
              src={src}
              alt={`${bucket.label} ${i + 1}`}
              loading="lazy"
              onClick={() => setActiveIndex(i)}
              style={{ cursor: "pointer" }}
            />
          ))}
        </div>
      </div>

      {/* Expanded fullscreen viewer */}
      {activeIndex !== null && (
        <div className="hp-showcase" role="dialog" aria-modal="true">
          <div
            className="hp-showcase-backdrop"
            onClick={() => setActiveIndex(null)}
          />
          <div
            className="hp-showcase-card"
            style={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <button
              className="hp-lightbox-btn left"
              onClick={() =>
                setActiveIndex((i) => (i - 1 + imgs.length) % imgs.length)
              }
            >
              ‹
            </button>

            <img
              src={imgs[activeIndex]}
              alt="Expanded view"
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />

            <button
              className="hp-lightbox-btn right"
              onClick={() =>
                setActiveIndex((i) => (i + 1) % imgs.length)
              }
            >
              ›
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


const Home = () => {
  const navigate = useNavigate();
  const API_BASE = useMemo(
    () => process.env.REACT_APP_FLUENCY_SERVER?.replace(/\/+$/, "") ?? "",
    []
  );
  const [crew] = useState(() =>
    heroImages.length ? heroImages[Math.floor(Math.random() * heroImages.length)] : null
  );

  const [notebook] = useState(() =>
    notebookImages.length ? notebookImages[0] : null
  );

  const go = (path) => navigate(path);

  const gotoTokens = () => {
    const el = document.getElementById("tokens-section");
    if (el) el.scrollIntoView({ behavior: "smooth" });
    else navigate("/purchase");
  };

  const [activeKey, setActiveKey] = useState(null);
  const activeBucket = activeKey ? INVENTORY[activeKey] : null;

  const auth = getAuth();
  const isAuthed = !!auth.currentUser;
  const [teaser, setTeaser] = useState(null); // {teaser, name, type, inventoryRemaining}
  const [teaserOpen, setTeaserOpen] = useState(false);

  const ranOnce = React.useRef(false);

  const [activePilot, setActivePilot] = useState(null);
  const [declined, setDeclined] = useState(false);
  useEffect(() => {
    if (activePilot) {
      const storedDecline = localStorage.getItem(`declined_${activePilot.id}`);
      if (storedDecline) setDeclined(true);
    }
  }, [activePilot]);
  useEffect(() => {
    if (ranOnce.current) return;      // guard StrictMode dev double-mount
    ranOnce.current = true;

    const ac = new AbortController();
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/offers/preview-public?channel=web-home`, {
          signal: ac.signal,
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || "preview_failed");
        if (data?.offer) {
          setTeaser(data.offer);
          setTeaserOpen(true);
        }
      } catch (e) {
        if (!ac.signal.aborted) setTeaserOpen(false);
      }
    })();
    return () => ac.abort();
  }, [API_BASE]);

  const [phase, setPhase] = useState("none");

  useEffect(() => {
    fetch("https://fluency.ispeakwell.ca/api/pilots")
      .then(r => r.json())
      .then(list => {
        const current = list.find(p => p.active);
        if (!current) return;
        setActivePilot(current);
        setPhase(getPilotPhase(current));
      })
      .catch(err => console.error("Pilot fetch error:", err));
  }, []);

  return (
    <div className="hp-root">
      {teaserOpen && teaser && !isAuthed && (
        <div className="home-teaser-modal" role="dialog" aria-modal="true">
          <div className="home-teaser-card">
            <div className="home-teaser-badge">Today only*</div>
            <h3>{teaser.name}</h3>
            <p>{teaser.teaser}</p>
            <div className="home-teaser-actions">
              <a className="primary" href="/signup">Claim your bonus</a>
              <button onClick={() => setTeaserOpen(false)}>Maybe later</button>
            </div>
            <div className="home-teaser-footnote">*New, first-time users can claim only while inventory lasts.</div>
          </div>
        </div>
      )}
      {/* Hero */}
      <header className="hp-hero">
        <div className="hp-hero-bg" />
        <div className="hp-hero-wrap">
          {crew && <img className="hp-crew" src={crew} alt="iSpeakWell showcase" />}
          <h1 className="hp-title">The iSpeakWell Project</h1>
          <h2 className="hp-sub">
            Real conversations. Real tools. A faster path to confident speech for English learners.
          </h2>
          <p className="hp-pain">
            Few or no practice partners? Hidden real talk? High-bar teachers? Doubt? We built a platform
            to flip that script. How do you want to use your English? For study? Work? To teach? To perform? 
            Whatever your English speaking performance dream, we'll help you get your voice out clearly and confidently.
          </p>
          <div className="hp-cta-row">
            <button className="hp-btn hp-btn-primary" onClick={() => go("/login")}>
              Login
            </button>
            <button className="hp-btn hp-btn-ghost" onClick={() => go("/signup")}>
              Sign Up
            </button>
            <button className="hp-btn hp-btn-ghost" onClick={gotoTokens}>
              Try 2 Free Tokens
            </button>
          </div>
          <div className="hp-bonus">
            Resume & interview tools are <strong>included</strong>—focus on speech <strong>first</strong>.
          </div>
          {/* === FLUENCY FUNNEL — “Shaken, Not Stirred” Introduction === */}
          <section className="hp-funnel">
            <div className="hp-funnel-bg" aria-hidden="true"></div>

            <div className="hp-funnel-inner">
              <h2 className="hp-funnel-title">The iSpeakWell Fluency Funnel</h2>
              <p className="hp-funnel-tag">
                A three-layer system engineered for one mission: <strong>speak with confidence.</strong>
              </p>

              {/* === Layer 1 === */}
              <div className="hp-funnel-block">
                <div className="hp-funnel-num">01</div>
                <h3 className="hp-funnel-head">Layer 1 — The Training Lab</h3>
                <p className="hp-funnel-copy">
                  Begin in a private sandbox where no one hears you but the tools.
                  Over <strong>35 self-guided functions</strong> help you drill pronunciation, analyze
                  phonemes, repeat micro-chunks of speech, explore transcripts, test vocabulary,
                  decode images, explore Wikipedia, practice listening, and warm up with AI until your voice becomes steady.
                </p>
                <p className="hp-funnel-copy">
                  Visualizers. Speech comparators. TTS engines. Fluency meters. A video player wired
                  for precision practice. Everything you need to sharpen your voice—quietly.
                </p>
                <p className="hp-funnel-copy">
                  You'll generate your English speech engine using the Speech Planner tool. 
                  When your voice is strong and your Engine is running smooth, you'll be ready for Layer 2.
                </p>
              </div>

              {/* === Layer 2 === */}
              <div className="hp-funnel-block">
                <div className="hp-funnel-num">02</div>
                <h3 className="hp-funnel-head">Layer 2 — The Fluency Arena</h3>
                <p className="hp-funnel-copy">
                  When the tools have done their job, step into the Arena: free practice rooms,
                  open conversations, and token-locked sessions run by trained discourse leaders.
                  This is where safe practice turns into real English, spoken in real time.
                </p>
                <p className="hp-funnel-copy">
                  It's not a stage—it's a mission briefing. You step in prepared and walk out
                  more fluent than you walked in.
                </p>
                <p className="hp-funnel-copy">
                  Here, you're testing your speech plans against the real world in a simulated environment—the chatroom.
                  In the chatroom, again, you are safe from the intense stares of L1 speakers, enabling you to maintain confidence
                  and lock in excellent performance metrics within your speech system.
                </p>
              </div>

              {/* === Layer 3 === */}
              <div className="hp-funnel-block">
                <div className="hp-funnel-num">03</div>
                <h3 className="hp-funnel-head">Layer 3 — Assessment & Elevation</h3>
                <p className="hp-funnel-copy">
                  Choose your plan—<strong>Growth, Serious, or Pro</strong>.  
                  Assessments, feedback cycles, structured challenges, and confidence-building
                  tasks pull you upward layer by layer.
                </p>
                <p className="hp-funnel-copy">
                  Complete Pro and you earn the right to contribute to the 
                  <strong> iSpeakWell Podcast</strong>. When your voice is ready for the world, it goes there.
                </p>
              </div>

              {/* Closing line */}
              <p className="hp-funnel-closer">
                Three layers. One objective.  
                <span>Find your English voice—smooth, precise, and unmistakably yours.</span>
              </p>
            </div>
          </section>
          <div className="hp-scrollcue">Explore your training ground ↓</div>
          {!declined && activePilot && (phase === "prelaunch" || phase === "active") && (
            <div className="pilot-banner upcoming">
              <h2>{activePilot.name}</h2>
              <p>
                {phase === "prelaunch"
                  ? "Upcoming Pilot — Enrollment Open!"
                  : "Pilot Now Active!"}
              </p>
              <p>
                {new Date(activePilot.startAt._seconds * 1000).toLocaleDateString()} –{" "}
                {new Date(activePilot.endAt._seconds * 1000).toLocaleDateString()}
              </p>
              {activePilot.inviteMessage && (
                <p className="pilot-message">{activePilot.inviteMessage}</p>
              )}
              <div className="pilot-actions">
                <button onClick={() => navigate("/signup")}>Join Pilot</button>
                <button
                  onClick={() => {
                    localStorage.setItem(`declined_${activePilot.id}`, "true");
                    setDeclined(true);
                  }}
                >
                  Not Now
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Mosaic of what you can actually do */}
      <section className="hp-mosaic" id="playground">
        {Object.entries(INVENTORY).map(([key, bucket]) => (
          <CategoryCard
            key={key}
            title={bucket.label}
            items={bucket.items}
            onClick={() => setActiveKey(key)}
          />
        ))}
      </section>

      {/* How it works */}
      <section className="hp-how">
        <h3 className="hp-sec-title">How it works</h3>
        <ol className="hp-steps">
          <li>
            <span>1</span> Join free speaking rooms to warm up and meet the crew.
          </li>
          <li>
            <span>2</span> Power up with AI tools when you want targeted practice.
          </li>
          <li>
            <span>3</span> Pick a plan to secure mic time, Harkness seats, and weekly coaching.
          </li>
        </ol>
      </section>

      {/* Fluent Archetype Simulator (immersive demo) */}
      <section className="hp-helpers" id="archetypes">
        <div className="hp-helpers-card" style={{ padding: 0 }}>
          <ArchetypeSimulator />
        </div>
      </section>

      {/* Plans (uses your existing PricingCards) */}
      <section className="hp-plans" id="plans">
        <h3>iSpeakWell is Free to Join</h3>
        <h3>When You Want Pro Level Help & Motivation </h3>
        <h3>↓</h3>
        <PricingCards onGotoTokens={gotoTokens} />
        <div ref={(el) => el && (el.id = "tokens-section")} />
      </section>

      {/* Helper Speakers program */}
      <section className="hp-helpers">
        <div className="hp-helpers-card">
          <h3>Helper Speakers Program</h3>
          <p>
            We seed the community with native speaker facilitators (~50 per 1000 learners). They host,
            nudge, and model everyday conversation—inviting you into natural English.
          </p>
          <div className="hp-cta-row">
            <button className="hp-btn hp-btn-primary" onClick={() => go("/login")}>
              Find a Room
            </button>
            <button className="hp-btn hp-btn-ghost" onClick={() => go("/careers")}>
              Become a Helper
            </button>
          </div>
        </div>
      </section>

      {/* Podcast funnel */}
      <section className="hp-podcast">
        <div className="hp-podcast-inner">
          {notebook && (
            <div className="hp-notebook">
              <img src={notebook} alt="Studio / notebook aesthetic" loading="lazy" />
            </div>
          )}
          <div className="hp-podcast-copy">
            <h3 className="hp-sec-title">The iSpeakWell Podcast</h3>
            <p>
              Hit Pro level and you can pitch a segment. We surface the best learner clips and
              decode useful internet content that’s usually opaque to ESL learners—then push it to
              TikTok, Instagram, WhatsApp, Facebook, and LinkedIn to showcase your growth.
            </p>
            <button className="hp-btn hp-btn-primary" onClick={() => go("/login")}>
              Explore the Studio
            </button>
            <div className="hp-video-wrap">
              <iframe
                width="100%"
                height="315"
                src="https://www.youtube.com/embed/QD_N81Swryo"
                title="The iSpeakWell Podcast — Episode 1"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison grid (keep your existing) */}
      <section className="hp-compare">
        <h3 className="hp-sec-title">How we compare</h3>
        <FluencyComparisonGrid />
      </section>

      <ShowcaseOverlay
        bucket={activeBucket}
        onClose={() => setActiveKey(null)}
        onOpenRoute={() => {
          if (activeBucket?.route) go(activeBucket.route);
        }}
      />

      {/* Footer links (preserve) */}
<footer className="HP-links-section">
  <button className="HP-link-button" onClick={() => go("/about")}>About</button>
  <button className="HP-link-button" onClick={() => go("/careers")}>Careers</button>
  <button className="HP-link-button" onClick={() => go("/tokens")}>Tokens</button>
  <button className="HP-link-button" onClick={() => go("/contactus")}>Contact Us</button>
  <button className="HP-link-button" onClick={() => go("/login")}>Login</button>
</footer>
</div>
);
};

export default Home;

